import { TDdpMedia } from "@/shared/types";
import { ReactNode, Ref, useEffect, useImperativeHandle, useRef, useState, VideoHTMLAttributes } from "react";
import { IconVideoPause, IconVideoPlay } from "../icon/tanqueray";

export function DdpVideo(props: {
    video: TDdpMedia;
    className?: string;
    children?: ReactNode;
    ref?: Ref<HTMLVideoElement>;
    lazyLoad?: boolean;
    hasButton?: boolean;
    once?: boolean;
} & VideoHTMLAttributes<HTMLVideoElement>) {

    const videoRef = useRef<HTMLVideoElement>(null);
    const { video: { mediaS, mediaM, mediaL, poster }, className, children, ref, lazyLoad = false, hasButton = false, once = false, onLoadedMetadata, ...rest
    } = props;
    useImperativeHandle(ref, () => videoRef.current!, [videoRef]);
    const _mediaM = mediaM || mediaS;
    const _mediaL = mediaL || _mediaM;
    function setUrl() {
        if (!videoRef.current) return;
        if (innerWidth < 744) {
            if (videoRef.current?.src !== mediaS.url)
                videoRef.current?.setAttribute('src', mediaS.url);
        } else if (innerWidth < 1024) {
            if (videoRef.current?.src !== _mediaM.url)
                videoRef.current?.setAttribute('src', _mediaM.url);
        } else {
            if (videoRef.current?.src !== _mediaL.url)
                videoRef.current?.setAttribute('src', _mediaL.url);
        }
    }
     function _lazyLoad() {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setUrl();
                    observer.unobserve(entry.target);
                }
            });
        });
        observer.observe(videoRef.current!);
    }
    useEffect(() => {
        if (!lazyLoad) {
            setUrl();
        } else {
            _lazyLoad();
        }
        window.addEventListener('resize', setUrl);
        return () => {
            window.removeEventListener('resize', setUrl);
        }
    }, [lazyLoad])
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    return <div className={`video-container ${className || ''}`}>
        <video poster={poster?.url} ref={videoRef} {...rest} playsInline onLoadedMetadata={(e) => {
            if (!props.autoPlay && videoRef.current) {
                videoRef.current.muted = true;
                videoRef.current.play();
                videoRef.current.pause();
                videoRef.current.muted = props.muted || false;
            }
            setIsLoaded(true);
            if (props.autoPlay || once) {
                if (videoRef.current) {
                    videoRef.current.muted = true;
                }

                videoRef.current?.play().then(() => {
                    setIsPlaying(true);
                })
            }
            if (onLoadedMetadata) {
                onLoadedMetadata(e);
            }

        }} onEnded={() => {
            if (once) {
                setIsPlaying(false);
            }
        }} />
        <div className="video-mask">
            {hasButton && <>
                {isLoaded && isPlaying && <IconVideoPause onClick={() => {
                    videoRef.current?.pause();
                    setIsPlaying(false);
                }} className="icon-play-or-pause" />}
                {
                    isLoaded && !isPlaying && <IconVideoPlay onClick={() => {
                        videoRef.current?.play();
                        setIsPlaying(true);
                    }} className="icon-play-or-pause" />
                }
            </>}
            {children}
        </div>
    </div>
}